import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { isJestOrStorybook } from '../../test-helpers/isJestOrStorybook';
import useWindowVisibility from './useWindowVisibility';

export const PING_RESOURCE = '/ping.txt';
const TIMEOUT_TIME_MS = 2000;
const onlinePollingInterval = 10000;

const checkOnlineStatus = async () => {
  // If the browser has no network connection return offline
  if (isJestOrStorybook()) return true;
  if (!navigator.onLine) return navigator.onLine;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_TIME_MS);

  try {
    await fetch(PING_RESOURCE, {
      method: 'GET',
      signal: controller.signal,
    });
    const timeNow = dayjs().unix();
    localStorage.setItem('lastOnline', timeNow.toString());
    return true;
  } catch (error: unknown) {
    if (
      !(error instanceof DOMException && error.name === 'AbortError') &&
      !(error instanceof TypeError)
    ) {
      console.warn('[OnlineStatus] Unexpected ping failure:', error);
    }
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

const OnlineStatusContext = React.createContext(true);

export const OnlineStatusProvider: React.FC = ({ children }) => {
  const [onlineStatus, setOnlineStatus] = useState<boolean>(false);
  const windowVisible = useWindowVisibility();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    Sentry.setContext('onlineStatus', { onlineStatus });
  }, [onlineStatus]);

  const checkStatus = useCallback(async () => {
    const online = await checkOnlineStatus();
    setOnlineStatus(online);
  }, []);

  const handleOffline = useCallback(() => {
    setOnlineStatus(false);
  }, []);

  const handleOnline = useCallback(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    checkStatus();

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    // Add polling incase of slow connection
    intervalRef.current = setInterval(checkStatus, onlinePollingInterval);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkStatus, handleOffline, handleOnline]);

  useEffect(() => {
    if (windowVisible) {
      checkStatus();
    }
  }, [windowVisible, checkStatus]);

  return (
    <OnlineStatusContext.Provider value={onlineStatus}>{children}</OnlineStatusContext.Provider>
  );
};

export const useOnlineStatus = () => {
  const store = useContext(OnlineStatusContext);
  return store;
};

export default useOnlineStatus;
